import { Button } from '@mui/material';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import UploadDialog from 'src/components/organisms/upload-dialog/upload-dialog';

import {
  CreateVisitFormValues,
  MAX_VISITORS,
} from '../../organisms/create-visit/schema/create-visit-validation-schema';

import { createEmptyVisitor } from '@/features/visits/utils/create-empty-visitor';
import { createVisitorsTemplateXlsxBlob } from '@/features/visits/utils/create-visitors-template-xlsx-blob';
import { useDialog } from '@/hooks/use-dialog';
import { ExcelIcon } from '@/icons/excel';
import { importVisitorsFromXlsx } from '@/workers/import-visitors-worker/import-visitors-from-xlsx';

export const ImportVisitorsDialogButton = ({
  onImportSuccess,
}: {
  onImportSuccess?: () => void;
}) => {
  const { t } = useTranslation(['common', 'createVisit']);
  const { setValue, watch } = useFormContext<CreateVisitFormValues>();
  const visitors = watch('visitors');
  const allowInviteToParking = watch('host')?.allowInviteToParking ?? false;
  const [isOpen, handleOpen, handleClose] = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDownloadTemplate = () => {
    const blob = createVisitorsTemplateXlsxBlob(allowInviteToParking);

    saveAs(blob, 'visitors-template.xlsx');
  };

  const handleSubmit = async (files: File[]) => {
    const [file] = files;

    if (!file) {
      return;
    }

    const maxVisitorsToImport = MAX_VISITORS - (visitors?.length ?? 0);

    if (maxVisitorsToImport <= 0) {
      return {
        errorMessage: t('createVisit:validation.maxVisitors', { count: MAX_VISITORS }),
      };
    }

    setIsSubmitting(true);

    const importResult = await importVisitorsFromXlsx(
      file,
      allowInviteToParking,
      maxVisitorsToImport,
    );

    if (!importResult.success) {
      const errorMessage =
        importResult.errorCode === 'invalid_or_missing_required_fields'
          ? t(`createVisit:importVisitors.errors.${importResult.errorCode}`, {
              rows: importResult.rowNumbers.join(', '),
            })
          : t(`createVisit:importVisitors.errors.${importResult.errorCode}`);

      setIsSubmitting(false);
      return {
        errorMessage,
      };
    }

    const importedVisitors = importResult.visitors.map(visitor => ({
      ...createEmptyVisitor(),
      ...visitor,
    }));

    setValue('visitors', [...visitors, ...importedVisitors], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    toast.success(t('createVisit:importVisitors.success'));
    setIsSubmitting(false);
    handleClose();
    onImportSuccess?.();
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      handleClose();
    }
  };

  return (
    <>
      <Button onClick={handleOpen} startIcon={<ExcelIcon />} type="button" variant="outlined">
        {t('createVisit:importVisitors.title')}
      </Button>
      <UploadDialog
        title={t('createVisit:importVisitors.title')}
        message={t('createVisit:importVisitors.description')}
        open={isOpen}
        onCancel={handleCancel}
        handleDownload={handleDownloadTemplate}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        fileDropzoneDescription={t('createVisit:importVisitors.dropXls')}
        filesAcceptTypes={{
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-excel': ['.xls'],
        }}
        maxFilesNumber={1}
      />
    </>
  );
};
